import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Share2, MoreHorizontal, Heart, Send } from 'lucide-react';
import { Pin } from '../types';
interface BottomSheetProps {
  pin: Pin | null;
  isOpen: boolean;
  onClose: () => void;
  onToggleLike: (id: string) => void;
}
export function BottomSheet({
  pin,
  isOpen,
  onClose,
  onToggleLike
}: BottomSheetProps) {
  const [comment, setComment] = useState('');
  if (!pin) return null;
  return <AnimatePresence data-id="element-15">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/60 z-[60] backdrop-blur-sm" data-id="element-16" />

          {/* Sheet */}
          <motion.div initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-[70] h-[85vh] md:h-[90vh] md:max-w-4xl md:mx-auto md:inset-x-0 md:rounded-t-2xl overflow-hidden flex flex-col" drag="y" dragConstraints={{
        top: 0
      }} dragElastic={0.2} onDragEnd={(_, info) => {
        if (info.offset.y > 100) onClose();
      }} data-id="element-17">
            {/* Drag Handle */}
            <div className="bg-white pt-3 pb-2 flex justify-center z-10 shrink-0 md:hidden" data-id="element-18">
              <div className="w-10 h-1 bg-gray-300 rounded-full" data-id="element-19" />
            </div>

            <div className="flex-1 overflow-y-auto overscroll-contain" data-id="element-20">
              <div className="flex flex-col md:flex-row h-full" data-id="element-21">
                {/* Image Section */}
                <div className="w-full md:w-1/2 bg-black flex items-center justify-center relative group" data-id="element-22">
                  <img src={pin.imageUrl} alt={pin.title} className="w-full h-auto max-h-[50vh] md:max-h-full object-contain" data-id="element-23" />
                  <button onClick={onClose} className="absolute top-4 left-4 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors md:hidden" data-id="element-24">
                    <X className="w-5 h-5" data-id="element-25" />
                  </button>
                </div>

                {/* Content Section */}
                <div className="w-full md:w-1/2 flex flex-col bg-white h-full" data-id="element-26">
                  <div className="p-6 md:p-8 flex-1 overflow-y-auto" data-id="element-27">
                    {/* Header Actions */}
                    <div className="flex justify-between items-center mb-8 sticky top-0 bg-white z-10 py-2" data-id="element-28">
                      <div className="flex gap-2" data-id="element-29">
                        <button className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-30">
                          <MoreHorizontal className="h-6 w-6 text-gray-900" data-id="element-31" />
                        </button>
                        <button className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-32">
                          <Share2 className="h-6 w-6 text-gray-900" data-id="element-33" />
                        </button>
                      </div>
                      <div className="flex gap-3" data-id="element-34">
                        <button onClick={() => onToggleLike(pin.id)} className={`px-4 py-2 rounded-full font-semibold transition-colors flex items-center gap-2 ${pin.isLiked ? 'bg-red-50 text-red-600' : 'bg-gray-100 hover:bg-gray-200 text-gray-900'}`} data-id="element-35">
                          <Heart className={`w-5 h-5 ${pin.isLiked ? 'fill-current' : ''}`} data-id="element-36" />
                          {pin.likes}
                        </button>
                        <button className="px-6 py-2 bg-red-600 text-white font-semibold rounded-full hover:bg-red-700 transition-colors" data-id="element-37">
                          Save
                        </button>
                      </div>
                    </div>

                    <div className="space-y-6" data-id="element-38">
                      <div data-id="element-39">
                        <a href="#" className="text-sm underline text-gray-500 hover:text-black mb-2 block" data-id="element-40">
                          source.com
                        </a>
                        <h2 className="text-3xl font-bold text-gray-900 leading-tight" data-id="element-41">
                          {pin.title}
                        </h2>
                      </div>

                      <p className="text-gray-700 text-lg leading-relaxed" data-id="element-42">
                        {pin.description}
                      </p>

                      {/* Creator */}
                      <div className="flex items-center justify-between py-4 border-t border-b border-gray-100" data-id="element-43">
                        <div className="flex items-center gap-3" data-id="element-44">
                          <img src={pin.user.avatar} alt={pin.user.name} className="w-12 h-12 rounded-full object-cover bg-gray-200" data-id="element-45" />
                          <div data-id="element-46">
                            <p className="font-bold text-gray-900 text-sm" data-id="element-47">
                              {pin.user.name}
                            </p>
                            <p className="text-gray-500 text-xs" data-id="element-48">
                              12.4k followers
                            </p>
                          </div>
                        </div>
                        <button className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-900 font-semibold rounded-full text-sm transition-colors" data-id="element-49">
                          Follow
                        </button>
                      </div>

                      {/* Comments Section */}
                      <div className="pt-2" data-id="element-50">
                        <h3 className="font-bold text-gray-900 mb-4 text-lg" data-id="element-51">
                          Comments
                        </h3>
                        <div className="space-y-4 mb-6" data-id="element-52">
                          <p className="text-gray-500 text-sm italic" data-id="element-53">
                            No comments yet! Be the first to share your
                            thoughts.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Comment Input */}
                  <div className="p-4 border-t border-gray-100 bg-white sticky bottom-0" data-id="element-54">
                    <div className="flex gap-3 items-center" data-id="element-55">
                      <img src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=100&q=80" alt="You" className="w-10 h-10 rounded-full object-cover" data-id="element-56" />
                      <div className="flex-1 relative" data-id="element-57">
                        <input type="text" placeholder="Add a comment" className="w-full pl-4 pr-12 py-3 bg-gray-100 rounded-full text-sm focus:bg-white focus:ring-2 focus:ring-gray-200 outline-none transition-all" value={comment} onChange={e => setComment(e.target.value)} data-id="element-58" />
                        <button disabled={!comment.trim()} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-red-600 disabled:opacity-50 disabled:hover:text-gray-400 transition-colors" data-id="element-59">
                          <Send className="w-5 h-5" data-id="element-60" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}